import React, { useState, useEffect } from 'react';
import styles from './CategoryTree.module.css';

interface Category {
  id: string;
  name: string;
  children?: Category[];
}

interface CategoryTreeProps {
  categories: Category[];
  selected: string[];
  onChange: (selected: string[]) => void;
  counts?: Record<string, number>;
}

const CategoryTree: React.FC<CategoryTreeProps> = ({
  categories,
  selected,
  onChange,
  counts = {},
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Preserve existing expanded state when categories prop updates.
    // Only add new categories as expanded by default so the UI doesn't
    // unexpectedly collapse when the `categories` prop changes.
    setExpanded(prev => {
      const nextExpanded: Record<string, boolean> = { ...prev };
      categories.forEach(c => {
        if (c.children && c.children.length > 0 && !(c.id in nextExpanded)) {
          nextExpanded[c.id] = true;
        }
      });
      return nextExpanded;
    });
  }, [categories]);

  const toggleExpand = (id: string) => {
    setExpanded(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const toggleCategory = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter(item => item !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const renderCategory = (category: Category, level = 0) => {
    const hasChildren = category.children && category.children.length > 0;
    // While searching, force groups open so matching children are always visible.
    const isExpanded = searchTerm.trim() !== '' || expanded[category.id];
    const isVisible = searchTerm === '' || 
      category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      category.children?.some(child => 
        child.name.toLowerCase().includes(searchTerm.toLowerCase())
      );

    if (!isVisible) return null;

    return (
      <div key={category.id} className={styles.categoryItem}>
        <div 
          className={styles.categoryLabel}
          style={{ paddingLeft: `${level * 16}px` }}
        >
          {hasChildren && (
            <button 
              onClick={() => toggleExpand(category.id)}
              className={styles.expandButton}
            >
              {isExpanded ? '−' : '+'}
            </button>
          )}
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={selected.includes(category.id)}
              onChange={() => toggleCategory(category.id)}
              className={styles.checkbox}
            />
            <span className={styles.categoryName}>{category.name}</span>
            <span className={styles.countBadge}>{counts[category.id] ?? 0}</span>
          </label>
        </div>
        {hasChildren && isExpanded && (
          <div className={styles.childrenContainer}>
            {category.children?.map(child => 
              renderCategory(child, level + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search categories..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
      </div>
      <div className={styles.categoryList}>
        {categories.map(category => renderCategory(category))}
      </div>
    </div>
  );
};

export default CategoryTree;
